import { Suspense, useEffect, useCallback, useState } from 'react'
import { ThemeProvider } from 'react-jss'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Routes from '@/router'
import Layout from '@/components/Layout'
import LoaderEngine from '@/components/LoaderEngine'
import Preview from '@/pages/Preview'
import Loader from '@/components/Loader'
import Portal from '@/components/__commons/Portal'
import * as routingActions from '@/actions/routing'
import * as optionsActions from '@/actions/language'
import * as navActions from '@/actions/nav'
import * as localeActions from '@/actions/locale'
import * as queryActions from '@/actions/mediaQuery'
import ExternalScriptsLoader from '@/components/ExternalScriptsLoader'
import useViewport from '@/hooks/useViewport'
import { theme } from '@/styles/style'
import MountTransition from '@/router/TransitionSwitch/MountTransition'

function App() {
  const location = useLocation()
  const [isPreview, setPreview] = useState(false)

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { routes, currentLanguage } = useSelector((state) => ({
    routes: state.routing.routes || [],
    currentLanguage: state.locale.currentLanguage,
  }), shallowEqual)

  useViewport()

  const dispatch = useDispatch()
  const fetchRouting = useCallback(() => dispatch(routingActions.fetchRouting()), [dispatch])
  const fetchStrings = useCallback(() => dispatch(optionsActions.fetchStrings()), [dispatch])
  const fetchMenuLocations = useCallback(() => dispatch(navActions.fetchMenuLocations()), [dispatch])
  const fetchLocales = useCallback(() => dispatch(localeActions.fetchLocales()), [dispatch])
  const setLanguage = useCallback(() => dispatch(localeActions.setLanguage()), [dispatch])
  const setQuery = useCallback((data) => dispatch(queryActions.setQuery(data)), [dispatch])
  const setQueryKey = useCallback((data) => dispatch(queryActions.setQueryKey(data)), [dispatch])

  useEffect(() => {
    setLanguage()
  }, [])

  useEffect(() => {
    if (currentLanguage !== null) {
      fetchRouting()
      fetchLocales()
      fetchStrings()
      fetchMenuLocations()
    }
  }, [currentLanguage])

  const setMQ = () => {
    if (window.matchMedia) {
      const medias = []
      const mediasInverse = []
      const mediaRange = []
      Object.entries(theme.media).map((item) => {
        const value = item[1]
        medias.push(value.replace('@media ', ''))
        return false
      })
      Object.entries(theme.mediaInverse).map((item) => {
        const value = item[1]
        mediasInverse.push(value.replace('@media ', ''))
        return false
      })
      Object.entries(theme.media).map((item, i) => {
        const key = item[0]
        mediaRange[key] = `${medias[i]} and ${mediasInverse[i]}`
        const setMedia = () => {
          if (medias[key].matches) {
            setQuery(key)
            setQueryKey(i)
          }
        }
        medias[key] = window.matchMedia(mediaRange[key])
        medias[key].addListener(() => {
          setMedia()
        })
        setMedia()
        return false
      })
    }
    return this
  }

  useEffect(() => {
    setMQ()
  }, [])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const postId = searchParams.get('pid')

    if (postId) setPreview(true)
  }, [])

  return !isPreview ? (
    <>
      <ExternalScriptsLoader />
      <ThemeProvider theme={theme}>
        {routes.length > 0 ? (
          <Layout>
            <Suspense fallback={<div />}>
              <Routes routes={routes} />
            </Suspense>
          </Layout>
        ) : null}
        <Portal className="loader-root">
          <LoaderEngine>
            <Loader theme={theme} />
          </LoaderEngine>
        </Portal>
      </ThemeProvider>
    </>
  ) : (
    <ThemeProvider theme={theme}>
      <Layout preview>
        <MountTransition>
          <Preview theme={theme} />
        </MountTransition>
      </Layout>
    </ThemeProvider>
  )
}

export default App
